import React from "react";
import PropTypes from "prop-types";

// The lever types the editor can add. Each maps to the canonical adjustment
// shape {type, target_id, mode, value} sent to the score endpoint.
const LEVER_TYPES = [
  { value: "total", label: "Total spend" },
  { value: "budget", label: "Budget" },
  { value: "driver", label: "Driver" },
  { value: "lump", label: "Lump sum" },
];

// defaultLever builds a fresh lever of the given type. pct levers store value as
// a fraction (0.10 = +10%); lump levers store an absolute EUR value.
function defaultLever(type) {
  switch (type) {
    case "budget":
      return { type: "budget", target_id: "", mode: "pct", value: 0 };
    case "driver":
      return { type: "driver", target_id: "", mode: "pct", value: 0 };
    case "lump":
      return { type: "lump", target_id: "total", mode: "eur", value: 0 };
    case "total":
    default:
      return { type: "total", mode: "pct", value: 0 };
  }
}

const INPUT_CLS =
  "rounded-ctrl border border-card-bd px-2 py-1 text-sm text-text-2 focus:outline-none focus:ring-2 focus:ring-primary";

// LeverEditor is a controlled list of scenario levers. The parent owns the
// `levers` array and is notified of every change via onChange(nextLevers).
// `budgets` (from the score response echo) populates the budget/lump pickers.
export default function LeverEditor({ levers, onChange, budgets }) {
  function addLever(type) {
    if (!type) return;
    onChange([...levers, defaultLever(type)]);
  }

  function updateLever(index, patch) {
    onChange(levers.map((lev, i) => (i === index ? { ...lev, ...patch } : lev)));
  }

  function removeLever(index) {
    onChange(levers.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-text-2">Levers</span>
        <select
          aria-label="Add lever"
          value=""
          onChange={(e) => {
            addLever(e.target.value);
          }}
          className={INPUT_CLS}
        >
          <option value="" disabled>
            + Add lever…
          </option>
          {LEVER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {levers.length === 0 && (
        <p className="text-sm text-text-3">
          No levers yet — add one to model a what-if against the baseline.
        </p>
      )}

      <ul className="space-y-2">
        {levers.map((lever, index) => (
          <li
            key={index}
            className="flex flex-wrap items-center gap-2 rounded-ctrl border border-card-bd px-2 py-2"
          >
            <span className="w-24 text-sm font-medium text-text-2">
              {LEVER_TYPES.find((t) => t.value === lever.type)?.label ||
                lever.type}
            </span>

            {/* Target control */}
            {lever.type === "budget" && (
              <select
                aria-label="Budget target"
                value={lever.target_id || ""}
                onChange={(e) =>
                  updateLever(index, { target_id: e.target.value })
                }
                className={INPUT_CLS}
              >
                <option value="" disabled>
                  Select budget…
                </option>
                {budgets.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}

            {lever.type === "driver" && (
              <input
                type="text"
                aria-label="Driver key"
                placeholder="driver key"
                value={lever.target_id || ""}
                onChange={(e) =>
                  updateLever(index, { target_id: e.target.value })
                }
                className={`${INPUT_CLS} w-40`}
              />
            )}

            {lever.type === "lump" && (
              <select
                aria-label="Lump target"
                value={lever.target_id || "total"}
                onChange={(e) =>
                  updateLever(index, { target_id: e.target.value })
                }
                className={INPUT_CLS}
              >
                <option value="total">Total</option>
                {budgets.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}

            {/* Value control */}
            {lever.mode === "pct" ? (
              <span className="inline-flex items-center gap-1">
                <input
                  type="number"
                  aria-label="Percent change"
                  step="1"
                  value={Math.round((Number(lever.value) || 0) * 100)}
                  onChange={(e) =>
                    updateLever(index, {
                      value: (Number(e.target.value) || 0) / 100,
                    })
                  }
                  className={`${INPUT_CLS} w-20`}
                />
                <span className="text-sm text-text-3">%</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <span className="text-sm text-text-3">€</span>
                <input
                  type="number"
                  aria-label="Euro amount"
                  step="100"
                  value={Number(lever.value) || 0}
                  onChange={(e) =>
                    updateLever(index, { value: Number(e.target.value) || 0 })
                  }
                  className={`${INPUT_CLS} w-28`}
                />
              </span>
            )}

            <button
              type="button"
              aria-label={`Remove ${lever.type} lever ${index + 1}`}
              onClick={() => removeLever(index)}
              className="ml-auto rounded-ctrl px-2 py-1 text-sm text-text-3 hover:text-danger"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

LeverEditor.propTypes = {
  /** Controlled array of canonical {type, target_id, mode, value} levers. */
  levers: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Called with the next levers array on any add/edit/remove. */
  onChange: PropTypes.func.isRequired,
  /** Capped budgets echoed by the score response, for the budget/lump pickers. */
  budgets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
};

LeverEditor.defaultProps = {
  budgets: [],
};
